import { ChangeEvent } from 'react';
import { createUseStyles } from 'react-jss';
import { useField } from 'formik';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { Offer, OwnRecruitmentOptions } from '@app/api';
import { CheckboxBlockField } from '@app/form';

import { InformationBox } from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    gap: theme.marginBase * 2,
    flexWrap: 'wrap',
  },
  radio: {
    width: 'auto',
    flexGrow: 1,
    minWidth: 200,
  },
  box: {
    marginTop: theme.marginBase * 2,
  },
  platform: {
    color: theme.colors.blueAccessible,
    fontWeight: 'bold',
    '&:hover, &:active, &:focus': {
      textDecoration: 'underline',
    },
  },
  fees: {
    color: theme.colors.greenAccessible,
  },
}));

interface Props {
  offer?: Offer;
}

export const OwnRecruitmentSection = ({ offer }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [_field, { value }, { setValue }] = useField('targetingOfferId');

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (value === OwnRecruitmentOptions.TANDEMZ_PAY && offer) {
      setValue(offer.id);
    } else {
      setValue(undefined);
    }
    return true;
  };

  return (
    <>
      <div className={classes.container}>
        <CheckboxBlockField
          onChange={onChange}
          value={OwnRecruitmentOptions.I_PAY}
          label={t('studySetup.generalSetup.ownIncentive.iPay')}
          type="radio"
          name="ownRecruitmentOption"
          className={classes.radio}
        />
        {!!offer && (
          <CheckboxBlockField
            onChange={onChange}
            value={OwnRecruitmentOptions.TANDEMZ_PAY}
            label={t('studySetup.generalSetup.ownIncentive.tandemzPay')}
            type="radio"
            name="ownRecruitmentOption"
            className={classes.radio}
          />
        )}
      </div>
      {!!value && value === offer?.id && (
        <InformationBox variant="question" className={classes.box}>
          <Trans
            i18nKey="studySetup.generalSetup.ownIncentive.tandemzPayInfo"
            components={{
              tremendous: (
                <a
                  className={classes.platform}
                  href="https://www.tremendous.com/catalog"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              glady: (
                <a
                  className={classes.platform}
                  href="https://www.glady.com/enseignes"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              fees: <strong className={classes.fees} />,
            }}
          />
        </InformationBox>
      )}
    </>
  );
};
